import React, { useState, useEffect } from 'react';
import ResearchConfigForm from './ResearchConfigForm';
import ResearchConfigCard from './ResearchConfigCard';

/**
 * AgentResearchTab — research config management scoped to an agent.
 *
 * Handles config CRUD only (create, edit, delete).
 * Run/poll/results live in PortfolioRunResearchTab on the portfolio page,
 * where the portfolio execution context (tokens, exchange) is available.
 */
export default function AgentResearchTab({ agentId, config, authToken }) {
  const [configs, setConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [loadingConfigs, setLoadingConfigs] = useState(true);

  const apiBaseUrl = config?.apiBaseUrl || window.location.origin.replace('/web', '');

  const authHeaders = authToken
    ? { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };

  useEffect(() => {
    if (agentId) fetchConfigs();
  }, [agentId]);

  const fetchConfigs = async () => {
    try {
      setLoadingConfigs(true);
      const response = await fetch(
        `${apiBaseUrl}/agents/${agentId}/research/config`,
        { headers: authHeaders }
      );
      if (!response.ok) throw new Error('Failed to fetch research configs');
      const data = await response.json();
      setConfigs(data);
      setActiveConfig(data.find(c => c.is_active) || null);
    } catch (err) {
      console.error('Failed to fetch research configs:', err);
    } finally {
      setLoadingConfigs(false);
    }
  };

  const handleConfigSaved = () => {
    fetchConfigs();
    setShowCreateForm(false);
    setEditingConfig(null);
  };

  return (
    <div className="space-y-6">
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Research Configuration</h3>
          {!showCreateForm && !activeConfig && !loadingConfigs && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors"
            >
              + Create Config
            </button>
          )}
        </div>

        {loadingConfigs ? (
          <div className="flex items-center justify-center p-8 bg-slate-800/50 border border-slate-700 rounded-xl">
            <svg className="animate-spin h-6 w-6 text-cyan-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : showCreateForm ? (
          <ResearchConfigForm
            agentId={agentId}
            apiBaseUrl={apiBaseUrl}
            authToken={authToken}
            config={null}
            onSaved={handleConfigSaved}
            onCancel={() => setShowCreateForm(false)}
          />
        ) : editingConfig ? (
          <ResearchConfigForm
            agentId={agentId}
            apiBaseUrl={apiBaseUrl}
            authToken={authToken}
            config={editingConfig}
            onSaved={handleConfigSaved}
            onCancel={() => setEditingConfig(null)}
          />
        ) : activeConfig ? (
          <ResearchConfigCard
            agentId={agentId}
            apiBaseUrl={apiBaseUrl}
            authToken={authToken}
            config={activeConfig}
            readOnly={true}
            onEdit={() => setEditingConfig(activeConfig)}
            onDeleted={fetchConfigs}
          />
        ) : (
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center">
            <svg className="w-12 h-12 mx-auto text-slate-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h4 className="text-slate-300 font-medium mb-2">No Research Config</h4>
            <p className="text-slate-400 text-sm mb-4 max-w-md mx-auto">
              Create a research configuration to enable research runs from the portfolio.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors"
            >
              Create Research Config
            </button>
          </div>
        )}
      </section>

      {/* Inactive configs list */}
      {configs.filter(c => !c.is_active).length > 0 && (
        <section>
          <h4 className="text-sm font-medium text-slate-400 mb-3">Inactive Configs</h4>
          <div className="space-y-2">
            {configs.filter(c => !c.is_active).map(c => (
              <ResearchConfigCard
                key={c.id}
                agentId={agentId}
                apiBaseUrl={apiBaseUrl}
                authToken={authToken}
                config={c}
                readOnly={true}
                onEdit={() => setEditingConfig(c)}
                onDeleted={fetchConfigs}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
